"use client";

import { Purchases } from "@revenuecat/purchases-js";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSupabase } from "./supabase-auth-provider";

const REVENUECAT_WEB_KEY =
	process.env.NEXT_PUBLIC_REVENUECAT_WEB_KEY ?? "";

const ENTITLEMENT_ID = "pro";

interface SubscriptionState {
	isProUser: boolean;
	isLoading: boolean;
	isInitialized: boolean;
}

interface SubscriptionContextValue extends SubscriptionState {
	purchases: Purchases | null;
	refreshCustomerInfo: () => Promise<void>;
	presentPaywall: (targetElement: HTMLElement) => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
	isProUser: false,
	isLoading: true,
	isInitialized: false,
	purchases: null,
	refreshCustomerInfo: async () => {},
	presentPaywall: async () => false,
});

export function RevenueCatWebProvider({ children }: { children: ReactNode }) {
	const { user } = useSupabase();
	const purchasesRef = useRef<Purchases | null>(null);
	const [state, setState] = useState<SubscriptionState>({
		isProUser: false,
		isLoading: true,
		isInitialized: false,
	});

	// Initialize or re-initialize when user changes
	useEffect(() => {
		if (!REVENUECAT_WEB_KEY) {
			setState({ isProUser: false, isLoading: false, isInitialized: true });
			return;
		}

		async function init() {
			try {
				const appUserId = user?.id ?? Purchases.generateRevenueCatAnonymousAppUserId();

				const purchases = Purchases.configure(
					REVENUECAT_WEB_KEY,
					appUserId,
				);
				purchasesRef.current = purchases;

				const customerInfo = await purchases.getCustomerInfo();
				const isPro = ENTITLEMENT_ID in customerInfo.entitlements.active;

				setState({
					isProUser: isPro,
					isLoading: false,
					isInitialized: true,
				});
			} catch (error) {
				console.error("RevenueCat web init error:", error);
				setState((prev) => ({
					...prev,
					isLoading: false,
					isInitialized: true,
				}));
			}
		}

		init();
	}, [user?.id]);

	const refreshCustomerInfo = useCallback(async () => {
		const purchases = purchasesRef.current;
		if (!purchases) return;

		try {
			const customerInfo = await purchases.getCustomerInfo();
			setState((prev) => ({
				...prev,
				isProUser: ENTITLEMENT_ID in customerInfo.entitlements.active,
			}));
		} catch (error) {
			console.error("Failed to refresh customer info:", error);
		}
	}, []);

	const presentPaywall = useCallback(
		async (targetElement: HTMLElement): Promise<boolean> => {
			const purchases = purchasesRef.current;
			if (!purchases) return false;

			try {
				const result = await purchases.presentPaywall({
					htmlTarget: targetElement,
				});
				const isPro =
					ENTITLEMENT_ID in result.customerInfo.entitlements.active;
				setState((prev) => ({ ...prev, isProUser: isPro }));
				return isPro;
			} catch {
				// User dismissed the paywall or an error occurred
				return false;
			}
		},
		[],
	);

	const value = useMemo<SubscriptionContextValue>(
		() => ({
			...state,
			purchases: purchasesRef.current,
			refreshCustomerInfo,
			presentPaywall,
		}),
		[state, refreshCustomerInfo, presentPaywall],
	);

	return (
		<SubscriptionContext.Provider value={value}>
			{children}
		</SubscriptionContext.Provider>
	);
}

export function useWebSubscription() {
	return useContext(SubscriptionContext);
}

export function useIsWebProUser() {
	const { isProUser } = useContext(SubscriptionContext);
	return isProUser;
}
